import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { supabaseUserMiddleware } from "@/middleware/supabase-user-middleware";
import { createClient } from "@/lib/supabase/admin";
import { Status } from "@/lib/supabase/enums";
import { SalesChannels } from "./channel";
import {
  ShippingMethodTypes,
  type ShippingMethod,
  type ShippingMethodType,
} from "./shipping-rules";

export const shippingMethodsQueryOptions = () =>
  queryOptions({
    queryKey: ["storefront", "shipping-methods"],
    queryFn: () => fetchShippingMethods(),
  });

/** The storefront's active shipping and pickup methods, in the merchant's order. */
export const fetchShippingMethods = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .handler(async ({ context: { userId, channel } }) => {
    if (channel !== SalesChannels.storefront) return [];

    const { data, error } = await createClient()
      .from("shipping_method")
      .select(
        "id, name, description, type, price_in_cents, free_from_in_cents, min_order_in_cents, countries, min_delivery_days, max_delivery_days"
      )
      .eq("user_id", userId)
      .eq("status", Status.active)
      .order("position", { ascending: true })
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Could not get shipping methods", error);
      throw new Error("Could not get shipping methods.");
    }

    return data.map(toShippingMethod);
  });

export function toShippingMethod(row: {
  id: string;
  name: string;
  description: string | null;
  type: string;
  price_in_cents: number;
  free_from_in_cents: number | null;
  min_order_in_cents: number;
  countries: string[];
  min_delivery_days: number | null;
  max_delivery_days: number | null;
}): ShippingMethod {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    type: (row.type === ShippingMethodTypes.pickup
      ? ShippingMethodTypes.pickup
      : ShippingMethodTypes.shipping) as ShippingMethodType,
    priceInCents: row.price_in_cents,
    freeFromInCents: row.free_from_in_cents,
    minOrderInCents: row.min_order_in_cents,
    countries: row.countries ?? [],
    minDeliveryDays: row.min_delivery_days,
    maxDeliveryDays: row.max_delivery_days,
  };
}
